import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Bell, Clock, PackageX, RefreshCw } from "lucide-react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiFetch } from "@/lib/api";
import type { ReminderRow } from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

const TYPE_LABELS: Record<ReminderRow["type"], string> = {
  handover: "交接确认",
  return: "归还确认",
  license: "软件许可",
  borrow: "借用到期",
  stock: "库存补货",
};

const SEVERITY_LABELS: Record<ReminderRow["severity"], string> = {
  overdue: "已逾期",
  due_soon: "即将到期",
  warning: "需关注",
};

const SEVERITY_VARIANT: Record<ReminderRow["severity"], "danger" | "warning" | "secondary"> = {
  overdue: "danger",
  due_soon: "warning",
  warning: "secondary",
};

export function RemindersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["reminders"],
    queryFn: () =>
      apiFetch<{
        items: ReminderRow[];
        counts: { total: number; overdue: number; dueSoon: number; warning: number };
        generatedAt: string;
      }>("/api/v1/reminders"),
  });
  const items = query.data?.items ?? [];
  const counts = query.data?.counts ?? { total: 0, overdue: 0, dueSoon: 0, warning: 0 };

  function openTarget(item: ReminderRow) {
    if (item.type === "handover") navigate({ to: "/requests" });
    else if (item.type === "return") navigate({ to: "/assets" });
    else if (item.type === "license") navigate({ to: "/licenses" });
    else if (item.type === "borrow") navigate({ to: "/assets" });
    else navigate({ to: "/consumables" });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="提醒中心"
        description="汇总交接超时、借用到期、软件许可到期与耗材低库存；提醒只读，不替代业务处理。"
        actions={
          <Button variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
            <RefreshCw className="h-4 w-4" />
            刷新
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="全部提醒" value={counts.total} icon={Bell} />
        <StatCard label="已逾期" value={counts.overdue} icon={AlertTriangle} accent="danger" />
        <StatCard label="即将到期" value={counts.dueSoon} icon={Clock} accent="warning" />
        <StatCard label="需关注" value={counts.warning} icon={PackageX} accent="success" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>提醒明细</CardTitle>
          <CardDescription>点击“去处理”跳转到对应业务页；超时交接不会自动签收。</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="space-y-2">
              {["r1", "r2", "r3", "r4"].map((key) => (
                <Skeleton key={key} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="暂无提醒" description="当前没有超时、到期或低库存事项。" />
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={SEVERITY_VARIANT[item.severity]}>
                        {SEVERITY_LABELS[item.severity]}
                      </Badge>
                      <Badge variant="outline">{TYPE_LABELS[item.type]}</Badge>
                      <span className="text-sm font-medium">{item.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{item.detail}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.dueAt ? `截止：${formatDateTime(item.dueAt)}` : "无截止时间"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openTarget(item)}>
                      去处理
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          await apiFetch("/api/v1/reminders/ack", {
                            method: "POST",
                            body: JSON.stringify({ keys: [item.id] }),
                          });
                          await queryClient.invalidateQueries({ queryKey: ["reminders"] });
                        } catch {
                          // Keep the item visible if acknowledgement fails.
                        }
                      }}
                    >
                      标记已读
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
